import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Package, Boxes, Search } from "lucide-react";
import { prettyCat, CATEGORY_MAP } from "@/lib/catalog";

const prettyMat = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const prettyKey = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function labelFor(cat, key, kind) {
  const c = CATEGORY_MAP[cat];
  if (!c || !key) return "";
  const arr = c[kind] || [];
  return arr.find((x) => x.key === key)?.label || prettyKey(key);
}

export default function Stock() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/stock").then(r => setData(r.data)); }, []);

  const detailed = data?.finished_products_detailed || [];
  const filtered = useMemo(() => {
    if (!q.trim()) return detailed;
    const s = q.toLowerCase();
    return detailed.filter((r) => {
      const parts = [
        prettyCat(r.category), labelFor(r.category, r.variant, "variants"),
        labelFor(r.category, r.color, "colors"), labelFor(r.category, r.size, "sizes"),
        r.custom_size, r.product_name,
      ].join(" ").toLowerCase();
      return parts.includes(s);
    });
  }, [detailed, q]);

  if (!data) return <div className="p-8 text-[#6E6E6A]">Loading…</div>;

  const matStock = data.material_stock || [];

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Inventory</div>
        <h1 className="font-display text-4xl tracking-tighter font-black">Stock</h1>
        <p className="text-[#6E6E6A] text-sm mt-2">Finished = Produced − Dispatched · Material = Purchased/Opening − Consumed</p>

        {/* Category-level finished summary */}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <Card className="border border-[#D8D8D5] shadow-none rounded-md p-0 overflow-hidden">
            <div className="p-5 border-b border-[#D8D8D5] flex items-center gap-2">
              <Package className="w-4 h-4 text-[#6E6E6A]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Finished — by category</div>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Produced</TableHead><TableHead>Dispatched</TableHead><TableHead>Available</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.finished_products.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#6E6E6A] py-6">No data</TableCell></TableRow>}
                {data.finished_products.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{prettyCat(r.category)}</TableCell>
                    <TableCell>{r.produced}</TableCell>
                    <TableCell>{r.dispatched}</TableCell>
                    <TableCell><Badge className={r.available > 0 ? "bg-[#5B7B61] text-white" : "bg-[#C85A42] text-white"}>{r.available}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="border border-[#D8D8D5] shadow-none rounded-md p-0 overflow-hidden">
            <div className="p-5 border-b border-[#D8D8D5] flex items-center gap-2">
              <Boxes className="w-4 h-4 text-[#6E6E6A]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Raw material stock</div>
            </div>
            {matStock.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6E6E6A]">
                No stock recorded yet.<br/>
                <span className="text-xs">Add opening stock or a purchase in the Purchases page.</span>
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Material</TableHead><TableHead>Purchased</TableHead><TableHead>Consumed</TableHead><TableHead>Available</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {matStock.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{prettyMat(r.material)}</TableCell>
                      <TableCell>{r.purchased} {r.unit}</TableCell>
                      <TableCell>{r.consumed} {r.unit}</TableCell>
                      <TableCell><Badge className={r.available > 0 ? "bg-[#5B7B61] text-white" : r.available < 0 ? "bg-[#C85A42] text-white" : "bg-[#E8E8E6] text-[#1C1C1A]"}>{r.available} {r.unit}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        {/* Detailed finished stock */}
        <Card className="border border-[#D8D8D5] shadow-none rounded-md p-0 overflow-hidden mt-6">
          <div className="p-5 border-b border-[#D8D8D5] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#6E6E6A]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Finished — detailed (variant · color · size)</div>
            </div>
            <div className="relative w-72">
              <Search className="w-3 h-3 text-[#6E6E6A] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search e.g. Onyx Classic 4×8" className="pl-8 h-8 text-xs" data-testid="stock-search" />
            </div>
          </div>
          {detailed.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#6E6E6A]">No detailed data yet — log production or dispatch with variant/color/size selected.</div>
          ) : (
            <div className="max-h-[540px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Category</TableHead><TableHead>Variant</TableHead><TableHead>Color</TableHead>
                  <TableHead>Size</TableHead><TableHead>Produced</TableHead><TableHead>Dispatched</TableHead>
                  <TableHead>Available</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#6E6E6A] py-6">No matches</TableCell></TableRow>}
                  {filtered.map((r, i) => (
                    <TableRow key={i} data-testid={`stock-row-${i}`}>
                      <TableCell className="text-xs">{prettyCat(r.category)}</TableCell>
                      <TableCell className="text-sm">{labelFor(r.category, r.variant, "variants") || "—"}</TableCell>
                      <TableCell className="text-sm">{labelFor(r.category, r.color, "colors") || "—"}</TableCell>
                      <TableCell className="text-sm">{r.size ? labelFor(r.category, r.size, "sizes") : (r.custom_size || "—")}</TableCell>
                      <TableCell>{r.produced} {r.unit}</TableCell>
                      <TableCell>{r.dispatched} {r.unit}</TableCell>
                      <TableCell><Badge className={r.available > 0 ? "bg-[#5B7B61] text-white" : r.available < 0 ? "bg-[#C85A42] text-white" : "bg-[#E8E8E6] text-[#1C1C1A]"}>{r.available} {r.unit}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
